import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import { SiteTheme } from '../../generated/types';
import { useHoverDirty } from 'react-use';
import { CSSTransition } from 'react-transition-group';
import styled from 'styled-components';
import { LoadingButton } from '@mui/lab';
import { isMobile } from 'react-device-detect';

interface ThemeCardProps {
    theme: SiteTheme;
    isSelected: boolean;
    isAddingMenu: boolean;
    onSelect: (theme: SiteTheme) => void;
    onUseTheme: () => void;
}

const Indicator = styled.section`
    transition: opacity 0.5s ease;
    &.fade-enter {
        opacity: 0;
    }

    &.fade-enter-active {
        opacity: 1;
    }

    &.fade-exit {
        opacity: 1;
    }

    &.fade-exit-active {
        opacity: 0;
    }
`;

export const ThemeCard: React.FC<ThemeCardProps> = ({ theme, isSelected, isAddingMenu, onSelect, onUseTheme }) => {
    const ref = useRef<HTMLDivElement>(null);
    const fadeRef = useRef<HTMLDivElement>(null);
    const hovered = useHoverDirty(ref);
    const [isInUse, setIsInUse] = useState(false);

    const onClickUseTheme = () => {
        setIsInUse(true);
        onUseTheme();
    };
    return (
        <>
            <div
                className={classNames({
                    'overflow-hidden rounded-12 relative cursor-pointer': true,
                })}
                onClick={() => onSelect(theme)}
                ref={ref}
                style={{
                    paddingBottom: isMobile ? `${1.38 * 100}%` : `${1.28 * 100}%`,
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                    }}
                >
                    <img
                        data-id={theme.id}
                        src={theme?.image}
                        className={`w-full border border-primary-20 rounded-12`}
                    />
                    <CSSTransition
                        nodeRef={fadeRef}
                        in={hovered || isInUse}
                        classNames={'fade'}
                        unmountOnExit
                        timeout={300}
                    >
                        <Indicator ref={fadeRef}>
                            <div
                                className={classNames({
                                    'absolute top-0 bottom-0 left-0 right-0 border-3 border-success-mid rounded-12 overflow-hidden':
                                        true,
                                    hidden: isMobile && !isSelected,
                                    block: !isMobile,
                                })}
                            >
                                <div className="h-full w-full bg-white bg-opacity-90 flex flex-col items-center justify-center space-y-10">
                                    <LoadingButton
                                        className="w-130"
                                        variant="contained"
                                        color="success"
                                        size="small"
                                        onClick={onClickUseTheme}
                                        loading={isAddingMenu}
                                        disabled={isAddingMenu}
                                    >
                                        <div className="text-white">Use Template</div>
                                    </LoadingButton>
                                </div>
                            </div>
                        </Indicator>
                    </CSSTransition>
                </div>
            </div>
            <div className="font-semibold text-primary-80 sm:mt-20 sm:mb-32 mt-14 mb-20 text-center">{theme.name}</div>
        </>
    );
};
